import React, { useState, useEffect, useCallback } from 'react';
import projectsData from './projects.json';

/* ── Helpers ─────────────────────────────────────────────────── */
const fixPath = (p) => p && p.replace(/^.*images\//, '/images/');

function useBodyLock(active) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

/* ── Icons ───────────────────────────────────────────────────── */
const ArrowIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);
const ExternalIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);
const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const ZoomIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803a7.5 7.5 0 0010.607 0zM10.5 7.5v6m3-3h-6" />
  </svg>
);

/* ── Project visual (image or gradient placeholder) ──────────── */
const ProjectVisual = ({ project, fill = false, className = '' }) => {
  const src = fixPath(project.image);
  const base = fill ? 'absolute inset-0 w-full h-full' : 'w-full h-full';

  if (!src) {
    return (
      <div className={`${base} bg-gradient-to-br ${project.accentGradient || 'from-slate-600 to-slate-800'} flex flex-col items-center justify-center gap-3 ${className}`}>
        <span className="text-white/10 font-black text-[6rem] leading-none select-none">
          {project.title.charAt(0)}
        </span>
        <span className="text-white/25 font-mono text-xs tracking-[0.2em] uppercase px-4 text-center">
          {project.title}
        </span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={project.title}
      className={`${base} object-cover ${className}`}
    />
  );
};

/* ── Lightbox (full-screen image view) ───────────────────────── */
const Lightbox = ({ src, alt, onClose }) => {
  useBodyLock(true);
  const close = useCallback(onClose, [onClose]);

  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [close]);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/97 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
      >
        <CloseIcon />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-w-full max-h-[90vh] rounded-xl object-contain shadow-2xl"
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
};

/* ── Detail Drawer (slides up on mobile, centered on desktop) ── */
const DetailModal = ({ project, onClose, onLightbox }) => {
  useBodyLock(true);
  const hasLink = project.link && project.link !== '#';
  const imgSrc  = fixPath(project.image);

  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      {/* Solid backdrop — prevents color mixing */}
      <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className="relative w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col bg-slate-900 border border-slate-700/60 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Image header */}
        <div className="relative h-52 sm:h-60 shrink-0 bg-slate-800">
          <ProjectVisual project={project} fill />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent pointer-events-none" />

          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 bg-slate-900/80 border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 rounded-xl flex items-center justify-center transition-all backdrop-blur-sm"
          >
            <CloseIcon />
          </button>

          {/* Zoom button (only if real image) */}
          {imgSrc && (
            <button
              onClick={() => onLightbox(imgSrc)}
              className="absolute top-4 right-16 w-9 h-9 bg-slate-900/80 border border-slate-700 text-slate-300 hover:text-sky-400 hover:border-sky-500/50 rounded-xl flex items-center justify-center transition-all backdrop-blur-sm"
              title="View full image"
            >
              <ZoomIcon />
            </button>
          )}

          {/* Badges */}
          <div className="absolute bottom-4 left-5 flex items-center gap-2">
            {project.year && (
              <span className="font-mono text-[10px] text-slate-300 bg-slate-950/90 border border-slate-700 px-2.5 py-1 rounded-lg backdrop-blur-sm">
                {project.year}
              </span>
            )}
            {hasLink && (
              <span className="flex items-center gap-1.5 font-mono text-[10px] text-emerald-400 bg-slate-950/90 border border-emerald-500/40 px-2.5 py-1 rounded-lg backdrop-blur-sm">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                </span>
                Live
              </span>
            )}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 sm:p-8">
            <h3 className="text-2xl font-bold text-slate-100 mb-2 leading-tight">{project.title}</h3>
            <p className="text-slate-400 text-[15px] leading-relaxed mb-8">{project.description}</p>

            <div className="mb-7">
              <p className="font-mono text-[10px] text-sky-400 uppercase tracking-[0.18em] mb-3">Technologies</p>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map(t => (
                  <span key={t} className="font-mono text-xs text-sky-300 bg-sky-400/8 border border-sky-400/20 px-3 py-1 rounded-lg">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <p className="font-mono text-[10px] text-sky-400 uppercase tracking-[0.18em] mb-4">Key Highlights</p>
              <ul className="space-y-3.5">
                {project.responsibilities.map((r, i) => (
                  <li key={i} className="flex gap-3 text-slate-400 text-sm leading-relaxed">
                    <span className="w-1.5 h-1.5 bg-sky-500 rounded-full shrink-0 mt-[7px]" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            {hasLink && (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-sky-500 hover:bg-sky-400 text-white font-semibold py-3.5 rounded-xl transition-colors text-sm"
              >
                Visit Live Project <ExternalIcon />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Project Card ─────────────────────────────────────────────── */
const ProjectCard = ({ project, onOpen }) => {
  const hasLink = project.link && project.link !== '#';

  return (
    <div className="group bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:shadow-2xl hover:shadow-black/50 hover:-translate-y-0.5">

      {/* Clickable image area */}
      <div
        className="relative h-48 sm:h-52 overflow-hidden bg-slate-800 cursor-pointer"
        onClick={() => onOpen(project)}
      >
        <div className="absolute inset-0">
          <ProjectVisual
            project={project}
            fill
            className="group-hover:scale-[1.04] transition-transform duration-500"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent" />

        {/* Year badge */}
        {project.year && (
          <span className="absolute bottom-3 left-3 font-mono text-[10px] text-slate-300 bg-slate-950/90 border border-slate-700/70 px-2 py-0.5 rounded-md backdrop-blur-sm">
            {project.year}
          </span>
        )}

        {/* Live badge */}
        {hasLink && (
          <span className="absolute top-3 right-3 flex items-center gap-1.5 font-mono text-[10px] text-emerald-400 bg-slate-950/90 border border-emerald-500/40 px-2 py-0.5 rounded-md backdrop-blur-sm">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            Live
          </span>
        )}

        {/* Hover reveal */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs font-semibold text-white bg-slate-900/80 border border-slate-600 px-3 py-1.5 rounded-lg backdrop-blur-sm">
            View details
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-5">
        <h3 className="text-slate-100 font-bold text-[15px] leading-snug mb-2">{project.title}</h3>
        <p className="text-slate-500 text-sm leading-relaxed mb-4 flex-1 line-clamp-2">
          {project.description}
        </p>

        {/* Tech tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {project.technologies.slice(0, 3).map(t => (
            <span key={t} className="font-mono text-[10px] text-sky-300/80 bg-sky-400/8 border border-sky-400/15 px-2 py-0.5 rounded-md">
              {t}
            </span>
          ))}
          {project.technologies.length > 3 && (
            <span className="font-mono text-[10px] text-slate-600 border border-slate-800 px-2 py-0.5 rounded-md">
              +{project.technologies.length - 3} more
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onOpen(project)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-sky-400 bg-slate-800 hover:bg-slate-800/80 border border-slate-700 hover:border-sky-500/40 py-2.5 rounded-xl transition-all"
          >
            Details <ArrowIcon />
          </button>
          {hasLink && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-sky-600 hover:bg-sky-500 py-2.5 rounded-xl transition-colors"
            >
              Live <ExternalIcon />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Main ─────────────────────────────────────────────────────── */
export default function Projects() {
  const [selected,    setSelected]    = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [showAll,     setShowAll]     = useState(false);

  const visible = showAll ? projectsData : projectsData.slice(0, 6);

  return (
    <section id="projects" className="py-16 lg:py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">

        <p className="font-mono text-sky-400 text-xs sm:text-sm tracking-[0.15em] mb-3">03. Projects</p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8 lg:mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-100 leading-tight">
            Things I've built
          </h2>
          <p className="text-slate-500 text-sm sm:text-right max-w-[200px]">
            Production apps shipped for real users.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((project, i) => (
            <ProjectCard key={i} project={project} onOpen={setSelected} />
          ))}
        </div>

        {!showAll && projectsData.length > 6 && (
          <div className="text-center mt-12">
            <button
              onClick={() => setShowAll(true)}
              className="text-sky-400 border border-sky-400/40 hover:bg-sky-400/8 hover:border-sky-400/70 text-sm font-medium px-8 py-3 rounded-xl transition-all"
            >
              Show all {projectsData.length} projects
            </button>
          </div>
        )}
      </div>

      {selected && (
        <DetailModal
          project={selected}
          onClose={() => setSelected(null)}
          onLightbox={(src) => { setLightboxSrc(src); }}
        />
      )}

      {lightboxSrc && (
        <Lightbox
          src={lightboxSrc}
          alt="Project screenshot"
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </section>
  );
}
